<template>
  <v-form
    ref="formRef"
    class="pv-form method-form"
    @submit.prevent=""
  >
    <!-- Anlagenstatus: only shown for reference -->
    <div v-if="reference" class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">
          mdi-check-circle-outline
        </v-icon>
        <span>Anlagenstatus</span>
      </div>

      <v-checkbox
        v-model="form.available"
        :error-messages="validationErrors?.available"
        label="Anlage vorhanden"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.available" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">
                mdi-information
              </v-icon>
            </template>

            <span>{{ hint.available }}</span>
          </v-tooltip>
        </template>
      </v-checkbox>

      <div class="section-description text-body-2 text-medium-emphasis">
        Aktivieren Sie diese Option, wenn bereits eine Photovoltaikanlage
        vorhanden ist.
      </div>
    </div>

    <!-- All remaining fields are shown for planned state or an available reference -->
    <template v-if="form.available || !reference">
      <!-- Standort -->
      <div class="method-section">
        <div class="method-section-heading">
          <v-icon size="22" color="primary">
            mdi-map-marker-outline
          </v-icon>
          <span>Standort</span>
        </div>

        <!--
          Der Standort kommt vom Projekt. Vier Zahlenfelder je Maßnahme
          (Referenz und Planung getrennt) für ein und dasselbe Gebäude
          einzugeben, war die Doppelerfassung, die hier entfällt.
        -->
        <div v-if="!useOwnLocation" class="inherited-location mb-3">
          <template v-if="projectHasLocation">
            <div class="d-flex align-center">
              <v-icon size="18" color="primary" class="me-2">mdi-map-marker-check-outline</v-icon>
              <span class="font-weight-medium">Standort aus dem Projekt</span>
            </div>

            <div v-if="projectAddress" class="text-body-2 mt-1">{{ projectAddress }}</div>

            <div class="text-caption text-medium-emphasis mt-1">
              {{ formatCoordinate(projectLatitude) }}, {{ formatCoordinate(projectLongitude) }}
            </div>
          </template>

          <template v-else>
            <div class="d-flex align-center">
              <v-icon size="18" color="warning" class="me-2">mdi-map-marker-alert-outline</v-icon>
              <span class="font-weight-medium">Kein Projektstandort hinterlegt</span>
            </div>

            <div class="text-caption text-medium-emphasis mt-1">
              Hinterlegen Sie die Adresse in den Projektdetails und ermitteln Sie dort den
              Standort — oder geben Sie hier einen abweichenden Standort an.
            </div>
          </template>
        </div>

        <v-checkbox
          v-model="useOwnLocation"
          density="compact"
          hide-details
          label="Abweichenden Standort verwenden"
          class="mb-2"
        />

        <v-text-field
          v-if="useOwnLocation"
          v-model.number="form.latitude"
          type="number"
          step="0.0001"
          :error-messages="validationErrors?.latitude"
          label="Breitengrad"
          suffix="°"
          :hint="hint.latitude"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip v-if="hint.latitude" location="bottom" color="surface" content-class="tooltip">
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.latitude }}</span>
            </v-tooltip>

            <v-btn
              v-if="!reference && referenceData?.latitude"
              icon
              variant="text"
              title="Referenzwert übernehmen"
              aria-label="Referenzwert für Breitengrad übernehmen"
              @click="copyFromReference('latitude')"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </v-text-field>

        <v-text-field
          v-if="useOwnLocation"
          v-model.number="form.longitude"
          type="number"
          step="0.0001"
          :error-messages="validationErrors?.longitude"
          label="Längengrad"
          suffix="°"
          :hint="hint.longitude"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip v-if="hint.longitude" location="bottom" color="surface" content-class="tooltip">
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.longitude }}</span>
            </v-tooltip>

            <v-btn
              v-if="!reference && referenceData?.longitude"
              icon
              variant="text"
              title="Referenzwert übernehmen"
              aria-label="Referenzwert für Längengrad übernehmen"
              @click="copyFromReference('longitude')"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </div>

      <!-- Anlage -->
      <div class="method-section">
        <div class="method-section-heading">
          <v-icon size="22" color="primary">
            mdi-solar-panel-large
          </v-icon>
          <span>Anlage</span>
        </div>

        <v-text-field
          v-model.number="form.amount"
          type="number"
          :error-messages="validationErrors?.amount"
          label="Anzahl"
          :hint="hint.amount"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip v-if="hint.amount" location="bottom" color="surface" content-class="tooltip">
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.amount }}</span>
            </v-tooltip>

            <v-btn
              v-if="!reference && referenceData?.amount"
              icon
              variant="text"
              title="Referenzwert übernehmen"
              aria-label="Referenzwert für Anzahl übernehmen"
              @click="copyFromReference('amount')"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </v-text-field>

        <v-text-field
          v-model.number="form.peak_power"
          type="number"
          :error-messages="validationErrors?.peak_power"
          label="Nennleistung"
          suffix="kWp"
          :hint="hint.peak_power"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip v-if="hint.peak_power" location="bottom" color="surface" content-class="tooltip">
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.peak_power }}</span>
            </v-tooltip>

            <v-btn
              v-if="!reference && referenceData?.peak_power"
              icon
              variant="text"
              title="Referenzwert übernehmen"
              aria-label="Referenzwert für Nennleistung übernehmen"
              @click="copyFromReference('peak_power')"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </v-text-field>

        <v-text-field
          v-model.number="form.surface_tilt"
          type="number"
          :error-messages="validationErrors?.surface_tilt"
          label="Neigung"
          suffix="°"
          :hint="hint.surface_tilt"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip v-if="hint.surface_tilt" location="bottom" color="surface" content-class="tooltip">
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.surface_tilt }}</span>
            </v-tooltip>
          </template>
        </v-text-field>

        <v-select
          v-model.number="form.surface_orientation"
          :items="orientationItems"
          :error-messages="validationErrors?.surface_orientation"
          label="Ausrichtung"
          :hint="hint.surface_orientation"
          class="mb-3"
        >
          <template #append>
            <v-tooltip v-if="hint.surface_orientation" location="bottom" color="surface" content-class="tooltip">
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.surface_orientation }}</span>
            </v-tooltip>
          </template>
        </v-select>

        <v-select
          v-model="form.pv_module"
          :items="moduleItems"
          :error-messages="validationErrors?.pv_module"
          label="Modultyp"
          :hint="hint.pv_module"
          class="mb-3"
        >
          <template #append>
            <v-tooltip v-if="hint.pv_module" location="bottom" color="surface" content-class="tooltip">
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.pv_module }}</span>
            </v-tooltip>
          </template>
        </v-select>

        <v-text-field
          v-model.number="systemLossPercent"
          type="number"
          :error-messages="validationErrors?.system_efficiency"
          label="Systemverluste"
          suffix="%"
          :hint="hint.system_efficiency"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip v-if="hint.system_efficiency" location="bottom" color="surface" content-class="tooltip">
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.system_efficiency }}</span>
            </v-tooltip>
          </template>
        </v-text-field>
      </div>

      <!-- Ertrag -->
      <div class="method-section">
        <div class="method-section-heading">
          <v-icon size="22" color="primary">
            mdi-weather-sunny
          </v-icon>
          <span>Ertrag</span>
        </div>

        <div class="section-description text-body-2 text-medium-emphasis">
          Der spezifische Ertrag wird aus Wetterdaten der Jahre
          {{ form.data_start_year }}–{{ form.data_end_year }} ermittelt und über
          diesen Zeitraum gemittelt. Sie können ihn durch einen eigenen
          Simulationswert ersetzen.
        </div>

        <v-btn
          class="mb-3"
          color="primary"
          variant="tonal"
          prepend-icon="mdi-cloud-download-outline"
          :loading="yieldLoading"
          :disabled="!canFetchYield"
          @click="fetchYield"
        >
          Ertrag über PVGIS berechnen
        </v-btn>

        <div
          v-if="yieldBlockedReason"
          class="section-description text-caption text-medium-emphasis mb-3"
        >
          {{ yieldBlockedReason }}
        </div>

        <v-alert
          v-if="yieldError"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          {{ yieldError }}
        </v-alert>

        <v-text-field
          v-model.number="form.specific_annual_yield"
          type="number"
          :error-messages="validationErrors?.specific_annual_yield"
          label="Spezifischer Ertrag"
          suffix="kWh/kWp·a"
          :hint="hint.specific_annual_yield"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip v-if="hint.specific_annual_yield" location="bottom" color="surface" content-class="tooltip">
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.specific_annual_yield }}</span>
            </v-tooltip>
          </template>
        </v-text-field>

        <div v-if="yieldSource" class="section-description text-caption text-medium-emphasis">
          {{ yieldSource }}
        </div>
      </div>

      <!-- Nutzung -->
      <div class="method-section">
        <div class="method-section-heading">
          <v-icon size="22" color="primary">
            mdi-home-lightning-bolt-outline
          </v-icon>
          <span>Nutzung</span>
        </div>

        <v-slider
          v-model.number="form.self_use_percentage"
          :min="0"
          :max="100"
          :step="1"
          :error-messages="validationErrors?.self_use_percentage"
          label="Eigenverbrauchsanteil"
          thumb-label
          class="mb-1"
        >
          <template #append>
            <div class="slider-value">
              {{ formatNumber(form.self_use_percentage) }} %
            </div>
          </template>
        </v-slider>

        <div class="section-description text-body-2 text-medium-emphasis mb-2">
          {{ hint.self_use_percentage }}
        </div>

        <v-checkbox
          v-model="form.sell_to_grid"
          :error-messages="validationErrors?.sell_to_grid"
          label="Einspeisung ins Netz (vergütet)"
          class="mb-1"
        >
          <template #append>
            <v-tooltip v-if="hint.sell_to_grid" location="bottom" color="surface" content-class="tooltip">
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.sell_to_grid }}</span>
            </v-tooltip>
          </template>
        </v-checkbox>

        <div class="section-description text-body-2 text-medium-emphasis">
          Ob die Einspeisung in der CO₂- und Primärenergiebilanz gutgeschrieben
          wird, gilt für das gesamte Projekt und wird in den
          <router-link :to="{ name: 'ProjectDetails' }">
            Projektdetails
          </router-link>
          eingestellt.
        </div>

        <!-- Energy split preview -->
        <div class="yield-preview">
          <div class="yield-preview-row">
            <span>Erzeugung</span>
            <strong>{{ formatNumber(generated) }} kWh/a</strong>
          </div>
          <div class="yield-preview-row">
            <span>Eigenverbrauch</span>
            <strong>{{ formatNumber(selfUsed) }} kWh/a</strong>
          </div>
          <div class="yield-preview-row">
            <span>{{ form.sell_to_grid ? 'Einspeisung' : 'Abgeregelt' }}</span>
            <strong>{{ formatNumber(surplus) }} kWh/a</strong>
          </div>
        </div>

        <v-alert
          v-if="!form.sell_to_grid && surplus > 0"
          type="info"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          Ohne Einspeisung verfällt der Überschuss von
          {{ formatNumber(surplus) }} kWh/a. Er wird weder vergütet noch
          in der CO₂-Bilanz berücksichtigt.
        </v-alert>
      </div>
    </template>

    <!-- Reference empty state -->
    <div
      v-else-if="reference"
      class="renewable-empty-state"
    >
      <v-icon
        size="44"
        color="primary"
        class="mb-3"
      >
        mdi-solar-power-variant-outline
      </v-icon>

      <div class="text-subtitle-1 font-weight-bold">
        Keine bestehende Anlage
      </div>

      <div class="text-body-2 text-medium-emphasis mt-1">
        Aktivieren Sie „Anlage vorhanden“, um Referenzwerte zu erfassen.
      </div>
    </div>
  </v-form>
</template>

<!--
  Plain JS like every other method form: `useBaseForm` is an untyped .js
  module, so `lang="ts"` turns its return value into an implicit `any` and
  vue-tsc rejects the file. That is also why `vue/block-lang` reports this
  block, in company with the other method forms.
-->
<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useBaseForm } from '@/composables/useBaseForm'
import { useProjectStore } from '@/stores/projectStore'
import api from '@/plugins/axios'

/* Props as required by parent (action.vue) */
const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  reference:  { type: Boolean, default: false },
  planned:    { type: Boolean, default: false },
  otherData:  { type: Object, default: () => ({}) },
  sharedData: { type: Object, default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) },
  methodValue: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

/* Frontend -> Backend field mapping */
const fields = {
  available:             'available',
  amount:                'amount',
  latitude:              'latitude',
  longitude:             'longitude',
  peak_power:            'peak_power',
  surface_tilt:          'surface_tilt',
  surface_orientation:   'surface_orientation',
  system_efficiency:     'system_efficiency',
  pv_module:             'pv_module',
  data_start_year:       'data_start_year',
  data_end_year:         'data_end_year',
  specific_annual_yield: 'specific_annual_yield',
  self_use_percentage:   'self_use_percentage',
  sell_to_grid:          'sell_to_grid',
}

/* useBaseForm integration */
const {
  form,
  hint,
  referenceData,
  copyFromReference,
  fetchHints,
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  defaults: {
    available: false,
    amount: 1,
    surface_tilt: 30,
    surface_orientation: 180,
    system_efficiency: 0.86,
    pv_module: 'cSi:Free standing 2025',
    data_start_year: 2014,
    data_end_year: 2024,
    specific_annual_yield: 0,
    self_use_percentage: 30,
    sell_to_grid: true,
  },
  reference:  computed(() => props.reference),
  planned:    computed(() => props.planned),
  shared:     computed(() => false), // not a shared section
  otherData:  computed(() => props.otherData),
  sharedData: computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (event, payload) => emit(event, payload),
})

/* ---------------------------------------------------------------- *
 * Standort
 *
 * Die Koordinaten gehören zum Projekt, nicht zur einzelnen Maßnahme:
 * Referenz und Planung beschreiben dasselbe Gebäude. Leere Felder in der
 * Maßnahme bedeuten „Projektstandort verwenden"; das entspricht genau
 * `PV.effective_latitude` / `effective_longitude` im Backend.
 * ---------------------------------------------------------------- */
const projectStore = useProjectStore()

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function formatCoordinate(value) {
  const number = toNumberOrNull(value)
  return number === null ? '—' : number.toFixed(4).replace('.', ',')
}

const projectLatitude = computed(() => toNumberOrNull(projectStore.currentProject?.latitude))
const projectLongitude = computed(() => toNumberOrNull(projectStore.currentProject?.longitude))

const projectHasLocation = computed(
  () => projectLatitude.value !== null && projectLongitude.value !== null
)

const projectAddress = computed(() => {
  const project = projectStore.currentProject
  if (!project) return ''

  const town = [project.postcode, project.city].filter(Boolean).join(' ')
  return [project.street, town].filter(Boolean).join(', ')
})

/* Was tatsächlich gerechnet wird: eigener Wert, sonst der des Projekts. */
const effectiveLatitude = computed(() => toNumberOrNull(form.latitude) ?? projectLatitude.value)
const effectiveLongitude = computed(() => toNumberOrNull(form.longitude) ?? projectLongitude.value)

const useOwnLocation = ref(false)
let ownLocationTouched = false

/*
 * Eine gespeicherte Maßnahme mit eigenen Koordinaten öffnet direkt im
 * Abweichungsmodus. Die Daten kommen asynchron, deshalb ein Watcher statt
 * einer Initialisierung beim Aufbau.
 */
watch(
  () => [form.latitude, form.longitude],
  ([latitude, longitude]) => {
    if (ownLocationTouched) return
    if (latitude === null || latitude === undefined) {
      if (longitude === null || longitude === undefined) return
    }
    useOwnLocation.value = true
    ownLocationTouched = true
  },
  { immediate: true }
)

watch(useOwnLocation, (own) => {
  ownLocationTouched = true

  if (own) {
    // Der Projektstandort ist der naheliegende Startpunkt für eine Abweichung.
    if (toNumberOrNull(form.latitude) === null) form.latitude = projectLatitude.value
    if (toNumberOrNull(form.longitude) === null) form.longitude = projectLongitude.value
  } else {
    // Leer heißt: erben.
    form.latitude = null
    form.longitude = null
  }
})

/*
 * PVGIS uses 0 = north / 180 = south, which is easy to get wrong.
 * Offer the compass points rather than a bare number field.
 */
const orientationItems = [
  { title: 'Süd (180°)', value: 180 },
  { title: 'Südost (135°)', value: 135 },
  { title: 'Südwest (225°)', value: 225 },
  { title: 'Ost (90°)', value: 90 },
  { title: 'West (270°)', value: 270 },
  { title: 'Nordost (45°)', value: 45 },
  { title: 'Nordwest (315°)', value: 315 },
  { title: 'Nord (0°)', value: 0 },
]

const moduleItems = ref([
  { title: 'Kristallines Silizium, freistehend', value: 'cSi:Free standing 2025' },
])

/*
 * The model stores an efficiency, users think in losses.
 * 0.86 efficiency <-> 14 % losses.
 */
const systemLossPercent = computed({
  get: () => Math.round((1 - (form.system_efficiency ?? 0.86)) * 1000) / 10,
  set: (value) => {
    const loss = Number(value)
    if (Number.isFinite(loss)) {
      form.system_efficiency = Math.round((1 - loss / 100) * 1000) / 1000
    }
  },
})

/* Energy split preview */
const generated = computed(() =>
  (Number(form.specific_annual_yield) || 0) *
  (Number(form.peak_power) || 0) *
  (Number(form.amount) || 0)
)
const selfUsed = computed(() =>
  generated.value * (Number(form.self_use_percentage) || 0) / 100
)
const surplus = computed(() => generated.value - selfUsed.value)

function formatNumber(value) {
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(value || 0)
}

/* PVGIS lookup */
const yieldLoading = ref(false)
const yieldError = ref('')
const yieldSource = ref('')

const canFetchYield = computed(() =>
  effectiveLatitude.value !== null &&
  effectiveLongitude.value !== null &&
  Number(form.peak_power) > 0
)

/* Ein deaktivierter Knopf ohne Begründung ist eine Sackgasse. */
const yieldBlockedReason = computed(() => {
  if (canFetchYield.value) return ''
  if (effectiveLatitude.value === null || effectiveLongitude.value === null) {
    return 'Für den PVGIS-Abruf fehlt der Standort. Ermitteln Sie ihn in den '
      + 'Projektdetails oder geben Sie oben einen abweichenden Standort an.'
  }
  return 'Für den PVGIS-Abruf wird die Nennleistung in kWp benötigt.'
})

async function fetchYield() {
  yieldLoading.value = true
  yieldError.value = ''
  try {
    const { data } = await api.post('/methods/pv/pvgis-yield/', {
      latitude: effectiveLatitude.value,
      longitude: effectiveLongitude.value,
      peak_power: Number(form.peak_power),
      surface_tilt: Number(form.surface_tilt),
      surface_orientation: Number(form.surface_orientation),
      system_efficiency: Number(form.system_efficiency),
      pv_module: form.pv_module,
      start_year: Number(form.data_start_year),
      end_year: Number(form.data_end_year),
    })
    form.specific_annual_yield = Math.round(data.specific_annual_yield * 10) / 10
    yieldSource.value =
      `${data.source}, ${data.mount_type ?? ''} ${data.technology ?? ''}, ` +
      `Zeitraum ${form.data_start_year}–${form.data_end_year} ` +
      `(${formatNumber(data.period_years)} Jahre), abgerufen am ` +
      new Date().toLocaleDateString('de-DE')
  } catch (error) {
    yieldError.value =
      error?.response?.data?.detail ||
      'Der Ertrag konnte nicht ermittelt werden. Bitte tragen Sie ihn manuell ein.'
  } finally {
    yieldLoading.value = false
  }
}

async function fetchModules() {
  try {
    const { data } = await api.get('/methods/pv/modules/')
    if (Array.isArray(data) && data.length) {
      moduleItems.value = data.map(([value, title]) => ({ title, value }))
    }
  } catch {
    // Keep the built-in default; the select stays usable.
  }
}

/* Behavior parity: when 'available' becomes false on reference form, zero out totals */
watch(
  () => form.available,
  (val) => {
    if (!val && props.reference) {
      form.amount = 0
      form.peak_power = 0
      form.specific_annual_yield = 0
      form.self_use_percentage = 0
      form.sell_to_grid = false
    }
  }
)

/* Expose validateForm for parent error flow */
const formRef = ref(null)
function validateForm() {
  return formRef.value?.validate?.()
}

defineExpose({
  validateForm,
  fields: Object.values(fields),
})

/* Lifecycle */
onMounted(async () => {
  await fetchHints()
  await fetchModules()
})
</script>

<style scoped lang="scss">
.pv-form {
  width: 100%;
}

/* Input fields */
.pv-form :deep(.v-field) {
  border-radius: 18px;
}

.pv-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Visual sections */
.method-section {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

.method-section:last-child {
  margin-bottom: 0;
}

/* Section headings */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;

  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;

  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);

  font-size: 1rem;
  font-weight: 700;
}

/* Checkbox */
.pv-form :deep(.v-checkbox) {
  padding: 0.35rem 0.65rem;
  border-radius: 18px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

/* Section description */
.section-description {
  padding: 0 0.65rem 0.35rem;
  line-height: 1.45;
}

.slider-value {
  min-width: 4.5rem;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* Energy split preview */
.yield-preview {
  margin-top: 0.75rem;
  padding: 0.75rem 0.9rem;
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;
  background-color: rgba(var(--v-theme-primary), 0.04);
}

.yield-preview-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.15rem 0;
  font-variant-numeric: tabular-nums;
}

/* Help and reference-copy actions */
.pv-form :deep(.v-input__append),
.pv-form :deep(.v-field__append-inner) {
  align-items: center;
}

.pv-form :deep(.v-input__append .v-icon),
.pv-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.pv-form :deep(.v-input__append .v-btn),
.pv-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.pv-form :deep(.v-input__append .v-btn:hover),
.pv-form :deep(.v-field__append-inner .v-btn:hover) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Empty reference state */
.renewable-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 2rem 1rem;
  border: 1px dashed rgba(var(--v-theme-primary), 0.24);
  border-radius: 24px;

  background-color: rgba(var(--v-theme-primary), 0.025);
  text-align: center;
}

.inherited-location {
  padding: 0.75rem 1rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.16);
  border-radius: 12px;

  background-color: rgba(var(--v-theme-primary), 0.04);
}

/* Responsive */
@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .renewable-empty-state {
    padding: 1.5rem 0.8rem;
    border-radius: 20px;
  }
}
</style>
